<template>
  <div class="card-list">
    <a-scrollbar
      :outer-style="{ flexGrow: 1, minHeight: 0 }"
      style="height: 100%; overflow: auto"
    >
      <div class="card-list__container">
        <div
          v-for="(item, index) in data"
          :key="getItemKey(item, index)"
          class="card-list__item"
          :class="{
            'card-list__item--clickable': clickable,
            'card-list__item--hover': hoverable,
          }"
          @click="handleItemClick(item, index)"
          @mouseenter="handleItemHover(item, index)"
        >
          <div class="card-list__card">
            <slot
              name="card"
              :item="item"
              :index="index"
              :click="() => handleItemClick(item, index)"
            >
              <!-- 统一卡片结构 -->
              <div class="card-list__card-content">
                <!-- 卡片头部区域 -->
                <div class="card-list__header">
                  <div class="card-list__header-content">
                    <slot name="title" :item="item" :index="index">
                      <AutoTooltip :content="getItemTitle(item)">
                        <div class="card-list__title">{{
                          getItemTitle(item)
                        }}</div>
                      </AutoTooltip>
                    </slot>
                    <slot name="description" :item="item" :index="index">
                      <p class="card-list__description">
                        {{ getItemDescription(item) }}
                      </p>
                    </slot>
                  </div>
                  <slot name="icon" :item="item" :index="index">
                    <div class="card-list__icon">
                      <slot name="icon-content" :item="item" :index="index">
                        <!-- 默认图标 -->
                        <div class="card-list__default-icon"></div>
                      </slot>
                    </div>
                  </slot>
                </div>

                <!-- 卡片主体内容区域 -->
                <div v-if="$slots.content" class="card-list__body">
                  <slot name="content" :item="item" :index="index"></slot>
                </div>

                <!-- 卡片底部区域 -->
                <div v-if="!hideFooter" class="card-list__footer">
                  <div class="card-list__meta">
                    <slot name="meta" :item="item" :index="index">
                      <!-- 默认时间显示 -->
                      <div
                        v-if="getItemUpdateTime(item)"
                        class="card-list__time"
                      >
                        <span class="card-list__time-label">{{
                          t('components.cardList.updatedAt')
                        }}</span>
                        <span class="card-list__time-value">{{
                          formatRelativeTime(getItemUpdateTime(item))
                        }}</span>
                      </div>
                    </slot>
                  </div>
                  <div class="card-list__actions">
                    <slot name="actions" :item="item" :index="index"></slot>
                  </div>
                </div>
              </div>
            </slot>
          </div>
        </div>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="card-list__loading">
        <a-spin :size="32" />
      </div>

      <!-- 空状态 -->
      <div
        v-if="!loading && (!data || data.length === 0)"
        class="card-list__empty"
      >
        <slot name="empty">
          <a-empty :description="t('components.cardList.empty')" />
        </slot>
      </div>
    </a-scrollbar>

    <!-- 分页组件 -->
    <div v-if="pagination && total > pageSize" class="card-list__pagination">
      <a-pagination
        v-model:current="currentPage"
        v-model:page-size="currentPageSize"
        :total="total"
        :show-total="showTotal"
        :show-page-size="showPageSize"
        :page-size-options="pageSizeOptions"
        @change="handlePageChange"
        @page-size-change="handlePageSizeChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import AutoTooltip from '@/components/auto-tooltip/index.vue';
  import { formatRelativeTime } from '@/utils';

  const { t } = useI18n();

  interface CardListProps {
    data?: any[];
    loading?: boolean;
    clickable?: boolean;
    hoverable?: boolean;
    hideFooter?: boolean;
    keyField?: string;
    titleField?: string;
    descriptionField?: string;
    updateTimeField?: string;
    pagination?: boolean;
    total?: number;
    pageSize?: number;
    currentPage?: number;
    showTotal?: boolean;
    showPageSize?: boolean;
    pageSizeOptions?: number[];
    customClass?: string;
  }

  interface CardListEmits {
    (e: 'item-click', item: any, index: number): void;
    (e: 'item-hover', item: any, index: number): void;
    (e: 'page-change', page: number): void;
    (e: 'page-size-change', size: number): void;
    (e: 'update:currentPage', page: number): void;
    (e: 'update:pageSize', size: number): void;
  }

  const props = withDefaults(defineProps<CardListProps>(), {
    data: () => [],
    loading: false,
    clickable: true,
    hoverable: true,
    hideFooter: false,
    keyField: 'id',
    titleField: 'name',
    descriptionField: 'description',
    updateTimeField: 'updateTime',
    pagination: false,
    total: 0,
    pageSize: 15,
    currentPage: 1,
    showTotal: true,
    showPageSize: true,
    pageSizeOptions: () => [15, 30, 50, 100],
    customClass: '',
  });

  const emit = defineEmits<CardListEmits>();

  const currentPage = ref(props.currentPage);
  const currentPageSize = ref(props.pageSize);

  // 监听外部传入的页码变化
  watch(
    () => props.currentPage,
    (newPage) => {
      currentPage.value = newPage;
    }
  );

  watch(
    () => props.pageSize,
    (newSize) => {
      currentPageSize.value = newSize;
    }
  );

  const getItemKey = (item: any, index: number): string | number => {
    return item[props.keyField] || index;
  };

  const getItemTitle = (item: any): string => {
    return item[props.titleField] || '';
  };

  const getItemDescription = (item: any): string => {
    return item[props.descriptionField] || '';
  };

  const getItemUpdateTime = (item: any): string => {
    return item[props.updateTimeField] || '';
  };

  const handleItemClick = (item: any, index: number) => {
    if (props.clickable) {
      emit('item-click', item, index);
    }
  };

  const handleItemHover = (item: any, index: number) => {
    if (props.hoverable) {
      emit('item-hover', item, index);
    }
  };

  const handlePageChange = (page: number) => {
    currentPage.value = page;
    emit('page-change', page);
    emit('update:currentPage', page);
  };

  const handlePageSizeChange = (size: number) => {
    currentPageSize.value = size;
    emit('page-size-change', size);
    emit('update:pageSize', size);
  };
</script>

<style lang="less" scoped>
  @import '@/assets/style/variables.less';

  .card-list {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;

    &__container {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
      gap: var(--spacing-4);
      width: 100%;
      padding: var(--spacing-2);
    }

    &__item {
      position: relative;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      height: fit-content;

      &--clickable {
        cursor: pointer;
      }

      &--hover {
        &:hover {
          transform: translateY(-4px);
        }
      }
    }

    &__card {
      background: var(--color-bg-3);
      border: 1px solid var(--color-border-1);
      border-radius: 12px;
      height: 200px;
      display: flex;
      flex-direction: column;
      position: relative;
      overflow: hidden;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(
          135deg,
          rgba(64, 158, 255, 0.05) 0%,
          rgba(64, 158, 255, 0.02) 100%
        );
        opacity: 0;
        transition: opacity 0.3s ease;
        pointer-events: none;
      }
    }

    &__item--hover:hover &__card {
      border-color: var(--color-primary-1);
      box-shadow: 0 8px 24px rgba(64, 158, 255, 0.15);

      &::before {
        opacity: 1;
      }
    }

    &__card-content {
      display: flex;
      flex-direction: column;
      height: 100%;
      padding: 20px;
    }

    &__header {
      display: flex;
      align-items: flex-start;
      gap: 20%;
      margin-bottom: 16px;
    }

    &__icon {
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(
        135deg,
        var(--color-primary-3),
        var(--color-primary-4)
      );
      box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
    }

    &__default-icon {
      width: 20px;
      height: 20px;
      background: rgba(255, 255, 255, 0.9);
      border-radius: 4px;
    }

    &__header-content {
      flex: 1;
      min-width: 0;
    }

    &__title {
      font-size: 16px;
      font-weight: 600;
      color: var(--color-text-1);
      margin-bottom: 8px;
      line-height: 1.4;
      word-break: break-word;
    }

    &__description {
      font-size: 13px;
      color: var(--color-text-2);
      line-height: 1.5;
      margin: 0;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-word;
    }

    &__body {
      flex: 1;
      margin-bottom: 16px;
      min-height: 0;
    }

    &__footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: auto;
      padding-top: 12px;
      border-top: 1px solid var(--color-border-1);
    }

    &__meta {
      flex: 1;
    }

    &__time {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: var(--color-text-1)-tertiary;

      &-label {
        opacity: 0.8;
      }

      &-value {
        font-weight: 500;
      }
    }

    &__actions {
      flex-shrink: 0;
      margin-left: 12px;
    }

    // 分页样式
    &__pagination {
      display: flex;
      justify-content: center;
      padding-top: 10px;
      border-top: 1px solid var(--color-border-1);
      flex-shrink: 0;
    }

    // 加载状态
    &__loading {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 200px;
      color: var(--color-text-2);
      padding: 12px;
    }

    // 空状态
    &__empty {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      min-height: 200px;
      color: var(--color-text-2);
      text-align: center;
      padding: 12px;

      &-icon {
        font-size: 48px;
        margin-bottom: 16px;
        opacity: 0.5;
      }

      &-text {
        font-size: 14px;
      }
    }

    // 响应式设计
    @media (max-width: 768px) {
      &__container {
        grid-template-columns: 1fr;
        gap: 8px;
        padding: 8px;
      }

      &__pagination {
        padding: 12px 8px;
      }

      &__card-content {
        padding: 16px;
      }

      &__header {
        gap: 10px;
        margin-bottom: 12px;
      }

      &__icon {
        width: 36px;
        height: 36px;
      }
    }

    @media (min-width: 769px) and (max-width: 1024px) {
      &__container {
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      }
    }

    @media (min-width: 1025px) {
      &__container {
        grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
      }
    }
  }
</style>
